import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";

import ConfirmActionModal from "components/common/ConfirmActionModal";
import Loader from "components/common/Loader";
import {
  CloseButton,
  DetailInfoLine,
  ModalBackdrop,
  ModalContent,
  ModalImageContainer,
  ModalTitle,
} from "components/common/modals/modal.styles";
import React, { useCallback, useState, useEffect } from "react";
import { confirmEventAttendance, setAttendingEventStatus } from "store/attendingEvents/attendingEvents.actions";
import { AttendingEvent, AttendingEventType } from "store/attendingEvents/attendingEvents.types";
import { useAppDispatch } from "store/hooks";
import { ActionType, LoadingState } from "store/types";
import { IEventData } from "types";
import * as S from "./index.styles";

interface AttendEventModalProps {
  event: IEventData | AttendingEvent;
  open: boolean;
  onClose: () => void;
  onConfirm: (status: AttendingEventType) => void;
}

const isIEventData = (event: IEventData | AttendingEvent): event is IEventData => {
  return 'activity' in event;
};

const AttendEventModal: React.FC<AttendEventModalProps> = ({ event, open, onClose, onConfirm }) => {
  const dispatch = useAppDispatch();
  const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.NONE);
  const [confirmModal, setConfirmModal] = useState<ActionType>(ActionType.NONE);
  const [error, setError] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Reset transition state when modal closes
  useEffect(() => {
    if (!open) {
      setIsTransitioning(false);
    }
  }, [open]);

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleConfirmAttendance = useCallback(async () => {
    try {
      setLoadingState(LoadingState.CONFIRMING);
      setError(null);
      if (isIEventData(event)) {
        await dispatch(confirmEventAttendance({
          activityId: event.activity.id
        })).unwrap();
      } else {
        await dispatch(setAttendingEventStatus({
          activityId: event.id,
          status: AttendingEventType.CONFIRMED
        })).unwrap();
      }
      setIsTransitioning(true);
      onConfirm(AttendingEventType.CONFIRMED);
    } catch (error) {
      console.error('Failed to confirm attendance:', error);
      setError('Failed to confirm attendance. Please try again.');
    } finally {
      setLoadingState(LoadingState.NONE);
    }
  }, [dispatch, event, onConfirm]);

  const handleRejectAttendance = useCallback(async () => {
    try {
      setLoadingState(LoadingState.DECLINING);
      setError(null);
      const activityId = isIEventData(event) ? event.activity.id : event.id;
      await dispatch(setAttendingEventStatus({
        activityId,
        status: AttendingEventType.REFUSED
      })).unwrap();
      setIsTransitioning(true);
      onConfirm(AttendingEventType.REFUSED);
    } catch (error) {
      console.error('Failed to reject attendance:', error);
      setError('Failed to decline attendance. Please try again.');
    } finally {
      setLoadingState(LoadingState.NONE);
    }
  }, [dispatch, event, onConfirm]);

  const handleConfirmModalAction = useCallback(() => {
    setConfirmModal(ActionType.NONE);
    if (confirmModal === ActionType.CONFIRM) handleConfirmAttendance();
    if (confirmModal === ActionType.DECLINE) handleRejectAttendance();
  }, [confirmModal, handleConfirmAttendance, handleRejectAttendance]);

  if (!open) return null;

  // Extract common properties based on event type
  const eventTitle = isIEventData(event) ? event.activity.activity_title : event.activity_title;
  const startDateTime = isIEventData(event) ? event.activity.start_date_time : event.start_date_time;
  const endDateTime = isIEventData(event) ? event.activity.end_date_time : event.start_date_time;
  const eventPlaces = isIEventData(event) ? event.places : event.places;

  const isButtonDisabled = loadingState !== LoadingState.NONE || isTransitioning;

  return (
    <>
      <ModalBackdrop>
        <ModalContent>
          <CloseButton onClick={onClose} disabled={isButtonDisabled}>×</CloseButton>
          <ModalTitle>Confirm Attendance</ModalTitle>

          {error && (
            <S.ErrorMessage>
              {error}
            </S.ErrorMessage>
          )}

          {isIEventData(event) && event.visuals?.[0]?.image_url && (
            <ModalImageContainer>
              <img
                src={event.visuals[0].image_url}
                alt={eventTitle}
              />
            </ModalImageContainer>
          )}

          <DetailInfoLine>
            <AccessTimeIcon />
            {`${new Date(startDateTime).toLocaleDateString()} 
            ${formatTime(startDateTime)} - ${formatTime(endDateTime)}`}
          </DetailInfoLine>

          {eventPlaces && eventPlaces.length > 0 && (
            <DetailInfoLine>
              <LocationOnIcon />
              {`${eventPlaces[0].place_city}, ${eventPlaces[0].place_state}`}
            </DetailInfoLine>
          )}

          <S.ButtonContainer>
            <S.RejectButton
              onClick={() => setConfirmModal(ActionType.DECLINE)}
              disabled={isButtonDisabled}
              variant="contained"
            >
              {loadingState === LoadingState.DECLINING ? (
                <S.LoaderContainer>
                  <Loader size={22} color="#FE2F67" />
                </S.LoaderContainer>
              ) : "Decline"}
            </S.RejectButton>
            <S.ConfirmButton
              onClick={() => setConfirmModal(ActionType.CONFIRM)}
              disabled={isButtonDisabled}
              variant="contained"
            >
              {loadingState === LoadingState.CONFIRMING ? (
                <S.LoaderContainer>
                  <Loader size={22} color="#fff" />
                </S.LoaderContainer>
              ) : "Confirm"}
            </S.ConfirmButton>
          </S.ButtonContainer>
        </ModalContent>
      </ModalBackdrop>
      <ConfirmActionModal
        open={confirmModal !== ActionType.NONE}
        onClose={() => setConfirmModal(ActionType.NONE)}
        onConfirm={handleConfirmModalAction}
        actionType={confirmModal}
      />
    </>
  );
};

export default AttendEventModal; 